import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
// import { RouterOutlet } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { RoomsService } from '@app/core/services/api/rooms/rooms.service';
import { Room } from '@app/core/interfaces/room';
import { TechnicianApiService } from '@app/core/services/api/user/technician/technician-api.service';
import { Technician } from '@app/core/interfaces/technician';
import { ApiResponse } from '@app/core/interfaces/response';
import { Subject, Subscription, forkJoin, timer, of, takeUntil } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DevicesApiService } from '@services/api/devices/devices-api.service';
import { Device } from '@interfaces/device';
import { Roles } from '@enums/Roles';
import { UserStorageService } from '@services/local-storage/user-storage/user-storage.service';
import {
  ArchivedConversation,
  ConversationArchiveService
} from '@app/core/services/conversation-archive/conversation-archive.service';
import { CompanyFilterComponent } from '@components/company-filter/company-filter.component';
import { CompanyFilterService } from '@services/company-filter/company-filter.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule, 
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    CompanyFilterComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private roomsPollingSubscription: Subscription | null = null;
  private readonly roomsPollingInterval = 5000;
  private techniciansLoaded = false;
  private roomsLoaded = false;
  private devicesLoaded = false;
  private userStorage = inject(UserStorageService);

  // Counts for cards
  // Everything loaded, then shown for the company chosen in the superadmin's "Azienda" menu (getters below)
  private allRooms: Room[] = [];
  private allDevices: Device[] = [];
  private allTechnicians: Technician[] = [];
  private allConversations: ArchivedConversation[] = [];
  protected companyFilter = inject(CompanyFilterService);
  
  // Table data
  get rooms(): Room[] {
    return this.allRooms.filter(room => this.companyFilter.matches(room.company?.id));
  }

  get devices(): Device[] {
    return this.allDevices.filter(device => this.companyFilter.matches(device.company));
  }

  get deviceCount(): number {
    return this.devices.length;
  }

  get technicianCount(): number {
    return this.allTechnicians.filter(technician => this.companyFilter.matches(technician.company?.id)).length;
  }

  get recentConversations(): ArchivedConversation[] {
    return this.allConversations.filter(call => this.companyFilter.matches(call.company_id)).slice(0, 5);
  }

  
  devicesAvailable = false;
  techniciansAvailable = false;
  conversationsAvailable = false;
  lastUpdated: Date | null = null;
  
  // Loading states
  isLoading: boolean = true;
  errorKey: string | null = null;

  constructor(
    private technicianService: TechnicianApiService,
    private roomsService: RoomsService,
    private devicesService: DevicesApiService,
    private conversationArchiveService: ConversationArchiveService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.stopRoomsPolling();
    this.destroy$.complete();
  }

  private loadDashboardData(): void {
    this.isLoading = true;
    this.errorKey = null;
    this.techniciansLoaded = false;
    this.roomsLoaded = false;
    this.devicesLoaded = false;

    this.stopRoomsPolling();
    this.startRoomsPolling();
    // Technicians can read neither the device registry, the conversation archive nor the technician list:
    // asking only returned 404s (and a red "error loading technicians" banner on their dashboard)
    this.userStorage.getUserStorage().catch(() => null).then(user => {
      if (user?.roles?.id === Roles.TECHNICIAN) {
        this.techniciansAvailable = false;
        this.allTechnicians = [];
        this.techniciansLoaded = true;
        this.allDevices = [];
        this.devicesAvailable = false;
        this.devicesLoaded = true;
        this.allConversations = [];
        this.conversationsAvailable = false;
        this.checkLoadingComplete();
        return;
      }
      this.techniciansAvailable = true;
      this.loadTechnicianCount();
      this.loadDevices();
      this.loadRecentConversations();
    });

  }

  private loadDevices(): void {
    this.devicesService.get_devices_list(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.allDevices = response.data ?? [];
          this.devicesAvailable = true;
          this.devicesLoaded = true;
          this.checkLoadingComplete();
        },
        error: () => {
          // Technician accounts intentionally cannot list the company device registry.
          this.allDevices = [];
          this.devicesAvailable = false;
          this.devicesLoaded = true;
          this.checkLoadingComplete();
        }
      });
  }

  private loadRecentConversations(): void {
    this.conversationArchiveService.getArchive()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: conversations => {
          this.allConversations = conversations;
          this.conversationsAvailable = true;
        },
        // Roles without the archive (technicians) get a 404: the card stays hidden
        error: () => {
          this.allConversations = [];
          this.conversationsAvailable = false;
        }
      });
  }

  private loadTechnicianCount(): void {
    this.technicianService.get_technicians_list_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<Technician[]>) => {
          this.allTechnicians = response.data ?? [];
          this.techniciansLoaded = true;
          this.checkLoadingComplete();
        },
        error: (err) => {
          console.error('Error loading technicians:', err);
          this.errorKey = 'dashboard.error_load_technicians';
          this.techniciansLoaded = true;
          this.checkLoadingComplete();
        }
      });
  }
  
  private checkLoadingComplete(): void {
    if (this.techniciansLoaded && this.roomsLoaded && this.devicesLoaded) {
      this.isLoading = false;
    }
  }

  get activeRoomCount(): number {
    return this.rooms.filter(room => this.getRoomStatus(room) === 'active').length;
  }

  get busyRoomCount(): number {
    return this.rooms.filter(room => this.getRoomStatus(room) === 'busy').length;
  }

  get pendingDeviceCount(): number {
    return this.devices.filter(device => !device.enabled).length;
  }

  get enabledDeviceCount(): number {
    return this.devices.filter(device => device.enabled).length;
  }

  private startRoomsPolling(): void {
    if (this.roomsPollingSubscription && !this.roomsPollingSubscription.closed) {
      return;
    }

    this.roomsPollingSubscription = timer(0, this.roomsPollingInterval)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          this.fetchRoomsData().pipe(
            catchError(err => {
              console.error('Error loading rooms:', err);
              this.errorKey = 'dashboard.error_load_rooms';
              return of(null);
            })
          )
        )
      )
      .subscribe(result => {
        if (result) {
          this.allRooms = this.mapRoomsWithConnections(result.rooms, result.connections);
          this.lastUpdated = new Date();
          if (this.errorKey === 'dashboard.error_load_rooms') {
            this.errorKey = null;
          }
        }

        if (!this.roomsLoaded) {
          this.roomsLoaded = true;
          this.checkLoadingComplete();
        }
      });
  }

  private stopRoomsPolling(): void {
    if (this.roomsPollingSubscription) {
      this.roomsPollingSubscription.unsubscribe();
      this.roomsPollingSubscription = null;
    }
  }

  private fetchRoomsData() {
    return forkJoin({
      rooms: this.roomsService.getRooms(false),
      connections: this.roomsService.getRoomActive(false)
    }).pipe(
      map(({ rooms, connections }) => ({
        rooms: rooms?.data ?? [],
        connections
      }))
    );
  }

  private mapRoomsWithConnections(rooms: Room[], connections: any): Room[] {
    const connectList = Array.isArray(connections) ? connections : [];

    return rooms.map(room => {
      const connectedItem = connectList.find((item: any) => item && item['connectionId'] === room.uuid);
      return {
        ...room,
        connected: !!connectedItem,
        connectionNumber: connectedItem ? connectedItem['connectionNumber'] : 0
      };
    });
  }
  
  refreshData(): void {
    this.loadDashboardData();
  }

  // A headset still in the room after the panel was reloaded is not listed as connected until it offers
  // again, so only a busy room (already in a call) is closed; the panel offers the call itself once inside
  canJoinRoom(room: Room): boolean {
    return this.getRoomStatus(room) !== 'busy' && !!room.uuid;
  }

  openRoom(room: Room): void {
    if (!this.canJoinRoom(room) || !room.uuid) {
      return;
    }
    this.router.navigate(['/support/room', room.uuid]);
  }

  openDevices(): void {
    this.router.navigate(['/devices']);
  }

  openConversations(): void {
    this.router.navigate(['/conversations']);
  }

  openRooms(): void {
    this.router.navigate(['/support/room/list']);
  }

  openTechnicians(): void {
    this.router.navigate(['/users/technicians']);
  }

  getRoomStatusClass(room: Room): string {
    const status = this.getRoomStatus(room);
    switch (status) {
      case 'active':
        return 'xr-badge--wait';
      case 'busy':
        return 'xr-badge--busy';
      default:
        return 'xr-badge--off';
    }
  }

  // "Tutte le aziende" for the superadmin: the rooms of several companies are mixed, so each row says whose it is
  get showCompany(): boolean {
    return this.companyFilter.isSuperadmin && this.companyFilter.selected === null;
  }

  // Full green only where a field technician is waiting: twelve green buttons in a row hid what mattered
  joinButtonClass(room: Room): string {
    return this.getRoomStatus(room) === 'active' ? 'xr-btn--primary' : 'xr-btn--secondary';
  }

  // il pulsante disabilitato dice "Occupata" solo se la stanza è davvero in chiamata;
  // se manca solo l'uuid resta un neutro "Apri stanza" disabilitato
  isRoomBusy(room: Room): boolean {
    return this.getRoomStatus(room) === 'busy';
  }

  getRoomStatusLabelKey(room: Room): string {
    const status = this.getRoomStatus(room);
    if (status === 'active') {
      return 'dashboard.room_status_active';
    }
    if (status === 'busy') {
      return 'dashboard.room_status_busy';
    }
    return 'dashboard.room_status_inactive';
  }

  private getRoomStatus(room: Room): 'inactive' | 'active' | 'busy' {
    if (room.connected) {
      if ((room.connectionNumber ?? 0) > 1) {
        return 'busy';
      }
      return 'active';
    }
    return 'inactive';
  }
}
